/**
 * "How do I let this page use my microphone?" — answered for the browser the
 * person is actually looking at.
 *
 * A blocked microphone is not something a page can fix or even re-ask about:
 * once the site is blocked the browser stops showing the prompt at all, and the
 * only way back is the browser's own site settings. Where those live differs
 * per browser (and the OS can block the browser itself on top of that), so a
 * generic "allow the microphone" leaves most people staring at the screen.
 */
/** the `t` from `useTranslation`, narrowed to what these helpers use */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

export type BrowserName = "chrome" | "edge" | "safari" | "firefox" | "other";
export type BrowserOs = "mac" | "windows" | "ios" | "android" | "other";

export interface BrowserInfo {
  name: BrowserName;
  os: BrowserOs;
}

/** Which browser, on which OS. Pure: the defaults only read what the browser
 *  says about itself, so a test can hand in any user agent it likes. */
export function detectBrowser(
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
  touchPoints: number = typeof navigator !== "undefined"
    ? navigator.maxTouchPoints || 0
    : 0
): BrowserInfo {
  const os: BrowserOs = /iPhone|iPad|iPod/.test(ua)
    ? "ios"
    : // iPadOS introduces itself as a Mac; only the touch screen gives it away
    /Macintosh/.test(ua) && touchPoints > 1
    ? "ios"
    : /Android/.test(ua)
    ? "android"
    : /Windows/.test(ua)
    ? "windows"
    : /Mac OS X|Macintosh/.test(ua)
    ? "mac"
    : "other";

  // Order matters: Edge also says "Chrome", Chrome also says "Safari", and
  // Opera / Samsung Internet say both — those get the generic steps rather
  // than directions to menus they do not have.
  const name: BrowserName = /Edg(e|A|iOS)?\//.test(ua)
    ? "edge"
    : /OPR\/|Opera|SamsungBrowser|YaBrowser|Vivaldi/.test(ua)
    ? "other"
    : /Firefox\/|FxiOS\//.test(ua)
    ? "firefox"
    : /Chrome\/|CriOS\/|Chromium\//.test(ua)
    ? "chrome"
    : /Safari\//.test(ua) && /Version\//.test(ua)
    ? "safari"
    : "other";

  return { name, os };
}

const BROWSER_NAMES: Record<Exclude<BrowserName, "other">, string> = {
  chrome: "Chrome",
  edge: "Edge",
  safari: "Safari",
  firefox: "Firefox",
};

/** Steps to unblock the microphone for this site, in the order to do them.
 *  `withReload: false` while a call is still coming up: a reload then cancels
 *  the call it was meant to rescue. */
export function micHelpSteps(
  t: Translate,
  info: BrowserInfo,
  { withReload = true }: { withReload?: boolean } = {}
): string[] {
  const steps: string[] = [];
  if (info.name === "chrome" || info.name === "edge") {
    steps.push(t("call_help_chrome_open"), t("call_help_set_allow"));
  } else if (info.name === "safari" && info.os === "ios") {
    steps.push(t("call_help_safari_ios_open"), t("call_help_set_allow"));
  } else if (info.name === "safari") {
    steps.push(t("call_help_safari_open"), t("call_help_set_allow"));
  } else if (info.name === "firefox") {
    steps.push(t("call_help_firefox_open"), t("call_help_firefox_unblock"));
  } else {
    steps.push(t("call_help_generic"));
  }
  // a changed permission only applies to a page loaded after it
  if (withReload) steps.push(t("call_help_reload"));
  // The browser can be allowed and still get nothing: the OS decides which
  // apps may use the microphone at all, and says so nowhere in the browser.
  const browser =
    info.name === "other" ? t("call_help_this_browser") : BROWSER_NAMES[info.name];
  if (info.os === "mac") steps.push(t("call_help_os_mac"));
  else if (info.os === "windows") steps.push(t("call_help_os_windows"));
  // Safari's microphone switch lives with the site; every other iOS browser is
  // an app with its own switch in the iPhone's settings
  else if (info.os === "ios" && info.name !== "safari")
    steps.push(t("call_help_os_ios", { browser }));
  else if (info.os === "android")
    steps.push(t("call_help_os_android", { browser }));
  return steps;
}
